import { getCustomStageQuest } from '@utils'
import { create } from 'zustand'

import {
  answerQuestDialogApi,
  collectStarApi,
  fetchQuestDialogApi,
  getQuestDetailsApi,
  submitQuestApi,
} from '@api'
import { base64Decode, hashData } from '@ds'
import { StarSize, webApp } from '@shared'
import {
  InventoryCard,
  InventoryCardDisplayType,
  MainInventoryBackendCard,
  MainInventoryMapper,
  useInventoryStoreBase,
} from '@store'

import { createSelectors, Mapper } from '../helpers'
import { QuestId } from './questId'
import { stagesByQuestId } from './stages'

export interface QuestStarBackend {
  id: number
  is_collected: boolean
  position_x: number
  position_y: number
  star_size: StarSize
}

export interface QuestHintBackend {
  id: number
  is_unlocked: boolean
  position: number
  quest_id: number
  quest_part_id: number
  star_price: number | null
  text_ru: string
  text_en: string
  ad_unlock_enabled: boolean
  linked_quest: QuestId
}

export interface QuestHint {
  id: number
  isUnlocked: boolean
  position: number
  questId: number
  questPartId: number
  starPrice: number | null
  textRu: string
  textEn: string
  adUnlockEnabled: boolean
  linkedQuest: QuestId
}

export interface QuestDetailsBackend {
  current_part: number
  hints: QuestHintBackend[]
  solution: {
    input: string
    solution_hash: string
  } | null
  solved: boolean
  stars: QuestStarBackend[]
}

export interface QuestDetails {
  availableStars: QuestStar[]
  hints: QuestHint[]
  solution: {
    input: string
    solutionHash: string
  } | null
  currentStage: number
  solved: boolean
}

export interface QuestStar {
  x: number
  y: number
  size: StarSize
  id: number
  isCollected: boolean
}

const QuestStarMapper: Mapper<QuestStarBackend, QuestStar> = {
  mapFromBackend: function (data) {
    return {
      x: data.position_x,
      y: data.position_y,
      size: data.star_size,
      id: data.id,
      isCollected: data.is_collected,
    }
  },
  mapToBackend(data) {
    return {
      id: data.id,
      is_collected: data.isCollected || false,
      position_x: data.x,
      position_y: data.y,
      star_size: data.size,
    }
  },
}

const QuestHintMapper: Mapper<QuestHintBackend, QuestHint> = {
  mapFromBackend(data) {
    return {
      id: data.id,
      isUnlocked: data.is_unlocked,
      position: data.position,
      questId: data.quest_id,
      questPartId: data.quest_part_id,
      starPrice: data.star_price,
      textRu: data.text_ru,
      textEn: data.text_en,
      linkedQuest: data.linked_quest,
      adUnlockEnabled: data.ad_unlock_enabled
    }
  },
  mapToBackend(data) {
    return {
      id: data.id,
      is_unlocked: data.isUnlocked,
      position: data.position,
      quest_id: data.questId,
      quest_part_id: data.questPartId,
      star_price: data.starPrice,
      text_ru: data.textRu,
      text_en: data.textEn,
      linked_quest: data.linkedQuest,
      ad_unlock_enabled: data.adUnlockEnabled
    }
  },
}

const QuestDetailsMapper: Mapper<QuestDetailsBackend, QuestDetails> = {
  mapFromBackend: function (data) {
    return {
      availableStars: data.stars.map((item) =>
        QuestStarMapper.mapFromBackend(item)
      ),
      hints: (data.hints ?? []).map((item) =>
        QuestHintMapper.mapFromBackend(item)
      ).sort((a: QuestHint, b: QuestHint) => a.position - b.position),
      solution: data.solution
        ? {
            input: base64Decode(data.solution.input),
            solutionHash: data.solution.solution_hash,
          }
        : null,
      currentStage: data.current_part,
      solved: data.solved,
    }
  },
  mapToBackend(data) {
    return {
      current_part: data.currentStage,
      hints: data.hints.map((item) => QuestHintMapper.mapToBackend(item)),
      solution: data.solution
        ? {
            input: data.solution.input,
            solution_hash: data.solution.solutionHash,
          }
        : null,
      solved: data.solved,
      stars: data.availableStars.map((item) =>
        QuestStarMapper.mapToBackend(item)
      ),
    }
  },
}

export interface QuestSubmitBackend {
  reward_item?: MainInventoryBackendCard
  solution: {
    quest_id: number
    quest_part_id: number
    solution: string
    user_id: number
  }
}

export interface QuestSubmit {
  rewardItem?: InventoryCard
  solution: {
    questId: number
    questPartId: number
    solution: string
    userId: number
  }
}

export interface QuestCollectStarBackend {
  reward_item?: MainInventoryBackendCard
}

export interface QuestDialogReward {
  app_files: string
  app_id: number
  bundle: string
  collection: string
  created_at: string
  description: string
  display_type: InventoryCardDisplayType
  game_only: boolean
  hidden: boolean
  icon_url: string
  issued: number
  item_def_id: string
  marketable: boolean
  max_supply: number
  name: string
  price: string
  promo: string
  store_hidden: boolean
  store_images: string
  store_tags: string
  supply: number
  tags: string
  tradable: boolean
  type: string
  updated_at: string
  rarity: number
}

export interface QuestDialogChoice {
  id: string
  text: string
  text_en: string
}

export interface QuestDialog {
  text: string
  text_en: string
  roleplay?: string
  roleplay_en?: string
  author?: {
    name: string
    name_en: string
    avatar_url: string
  }
  choices?: QuestDialogChoice[]
  reward_items?: QuestDialogReward[]
}

export type DialogHistoryItem =
  | { type: 'question'; data: QuestDialog }
  | { type: 'answer'; text: string; text_en: string; id: string }

interface QuestState extends QuestDetails {
  isLoading: boolean
  isError: boolean
  questId: string | null
  questDialog: DialogHistoryItem[] | null
  isDialogComplete: boolean
  stages: number[][] | null
  actions: {
    setIsLoading: (val: boolean) => void
    setQuestDialog: (questId: QuestId) => Promise<boolean>
    answerQuestDialog: (questId: QuestId, answerId: string) => Promise<void>
    resetQuestDialog: () => void
    init: (id: QuestId) => void
    toNextStage: () => void
    checkSolution: (
      solutionVariant: string,
      shouldAddIgnoreInputInHash?: boolean
    ) => Promise<boolean>
    collectStar: (starId: QuestStar['id']) => Promise<{ ok: boolean }>
    reset: () => void
    fetch: () => Promise<void>
  }
}

const useMainQuestStoreBase = create<QuestState>((set, get) => ({
  isLoading: true,
  isError: false,
  availableStars: [] as QuestStar[],
  hints: [] as QuestHint[],
  stages: [] as number[][],
  solution: null,
  questDialog: null,
  isDialogComplete: false,
  currentStage: 0,
  questId: null,
  solved: false,
  actions: {
    setIsLoading: (val: boolean) => {
      set(() => ({ isLoading: val }))
    },
    setQuestDialog: async (questId: QuestId) => {
      const { data, ok } = await fetchQuestDialogApi(questId)
      if (ok && data && data.text) {
        set(() => ({
          questDialog: [{ type: 'question', data }],
          isDialogComplete: false,
        }))
      }
      return ok && !!(data?.text || data?.choices?.length)
    },

    answerQuestDialog: async (questId: QuestId, answerId: string) => {
      const currentDialog = get().questDialog
      if (!currentDialog || currentDialog.length === 0) return

      const lastQuestion = currentDialog
        .slice()
        .reverse()
        .find((item) => item.type === 'question')
      if (!lastQuestion || lastQuestion.type !== 'question') return

      const selectedAnswer = lastQuestion.data.choices?.find(
        (choice) => choice.id === answerId
      )
      if (!selectedAnswer) return

      const updatedHistory: DialogHistoryItem[] = [
        ...currentDialog,
        {
          type: 'answer',
          text: selectedAnswer.text,
          text_en: selectedAnswer.text_en,
          id: selectedAnswer.id,
        },
      ]

      set({ questDialog: updatedHistory })

      const { data, ok } = await answerQuestDialogApi(questId, answerId)
      if (ok) {
        if (data && data.text) {
          data.reward_items?.forEach((reward) => {
            useInventoryStoreBase
              .getState()
              .actions.addCard(
                MainInventoryMapper.mapFromBackend({
                  ...reward,
                  item_id: reward.item_def_id,
                })
              )
          })
          const hasNoChoices = data.choices && data.choices.length > 0
          set({
            questDialog: [...updatedHistory, { type: 'question', data }],
            isDialogComplete: !hasNoChoices,
          })
        }
      }
    },

    resetQuestDialog: () => {
      set({ questDialog: null })
    },

    toNextStage: () => {
      set((state) => {
        if (state.stages) {
          return {
            currentStage: Math.min(
              state.currentStage + 1,
              state.stages.flat().length - 1
            ),
          }
        }

        return state
      })
    },
    checkSolution: async (
      solutionVariant: string,
      shouldAddIgnoreInputInHash?: boolean
    ) => {
      const { solution, questId, currentStage } = get()
      if (!solution || !questId) return false

      const result = shouldAddIgnoreInputInHash
        ? solutionVariant
        : solution.input + solutionVariant

      const hash = await hashData(result)

      if (solution.solutionHash !== hash) return false

      const { data, ok } = await submitQuestApi(
        result,
        questId,
        currentStage + 1
      )

      if (ok) {
        if (data?.reward_item) {
          useInventoryStoreBase
            .getState()
            .actions.addCard(
              MainInventoryMapper.mapFromBackend(data.reward_item)
            )
        }
        const { data: questData, ok: questOk } = await getQuestDetailsApi(
          questId
        )

        if (questOk && questData) {
          webApp.HapticFeedback.notificationOccurred('success')
          useInventoryStoreBase.getState().actions.useReservedCard()

          set((prev) => {
            const questInfo = QuestDetailsMapper.mapFromBackend(questData)

            return {
              ...questInfo,
              currentStage: prev.currentStage,
              availableStars: questInfo.availableStars.filter(
                (item) => !item.isCollected
              ),
            }
          })

          get().actions.toNextStage()
        }
      }

      return false
    },
    collectStar: async (starId: number) => {
      const { availableStars, questId, currentStage } = get()

      if (!questId) return { ok: false }

      const newAvailableStars = availableStars.filter(
        (star) => star.id !== starId
      )
      set({ availableStars: newAvailableStars })

      try {
        const { ok, data } = await collectStarApi(questId, currentStage, starId)

        if (ok) {
          if (data?.reward_item) {
            useInventoryStoreBase
              .getState()
              .actions.addCard(
                MainInventoryMapper.mapFromBackend(data.reward_item)
              )
          }

          if (newAvailableStars.length === 0) {
            get().actions.toNextStage()
          }

          return { ok: true }
        }

        set({ availableStars })

        return { ok: false }
      } catch (e) {
        set({ availableStars })

        return { ok: false }
      }
    },
    init: async (questId: QuestId) => {
      if (!stagesByQuestId[questId]) {
        throw new Error('stages not found')
      }
      set({ isLoading: true, questId })

      try {
        const { data, ok } = await getQuestDetailsApi(questId)

        if (ok && data) {
          set(() => {
            const questInfo = QuestDetailsMapper.mapFromBackend(data)
            const availableStar = questInfo.availableStars.filter(
              (item) => !item.isCollected
            )

            return {
              ...questInfo,
              availableStars: availableStar,
              currentStage: calculateCurrentStage(questInfo, questId),
              stages: stagesByQuestId[questId],
              isLoading: false,
              questId,
            }
          })
        } else {
          set({ isLoading: false, isError: true })
        }
      } catch (e) {
        set({ isLoading: false, isError: true })
      }
    },
    fetch: async () => {
      const questId = get().questId

      if (!questId) return

      try {
        const { data, ok } = await getQuestDetailsApi(questId)

        if (ok && data) {
          set(() => {
            const questInfo = QuestDetailsMapper.mapFromBackend(data)
            const availableStar = questInfo.availableStars.filter(
              (item) => !item.isCollected
            )

            return {
              ...questInfo,
              availableStars: availableStar,
              currentStage: calculateCurrentStage(
                questInfo,
                questId as QuestId
              ),
              isLoading: false,
            }
          })
        } else {
          set({ isError: true })
        }
      } catch (e) {
        set({ isError: true })
      }
    },
    reset: () => {
      set({
        isLoading: true,
        isError: false,
        availableStars: [],
        hints: [],
        solution: null,
        currentStage: 0,
        questId: null,
        stages: [],
      })
      useInventoryStoreBase.getState().actions.clearReservedCards()
    },
  },
}))

export const useMainQuestActions = () =>
  useMainQuestStoreBase((state) => state.actions)
export const useMainQuestIsLastStage = () =>
  useMainQuestStoreBase((state) => {
    const flatStagesLength = state.stages?.flat().length ?? 0
    return state.currentStage === flatStagesLength - 1
  })
export const useMainQuestSolutionState = () =>
  useMainQuestStoreBase((state) => state.solution)
export const useMainQuestLoadingState = () =>
  useMainQuestStoreBase((state) => state.isLoading)
export const useMainQuest = createSelectors(useMainQuestStoreBase)

function calculateCurrentStage(questInfo: QuestDetails, questId?: QuestId) {
  const availableStar = questInfo.availableStars.filter(
    (item) => !item.isCollected
  )

  if (questId) {
    const customStage = getCustomStageQuest[questId]?.store?.(
      questInfo,
      availableStar
    )
    if (customStage !== undefined) {
      return customStage
    }
  }

  if (availableStar?.length || !questInfo.solution) {
    return questInfo.currentStage
  }

  return questInfo.currentStage - 1
}
